import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  SpinnerGap, 
  User, 
  EnvelopeSimple, 
  Lock, 
  Phone, 
  MapPin, 
  Buildings,
  Wrench,
  GoogleLogo,
  Briefcase,
  HardHat,
  House
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';
import { trackConversion } from '../utils/analytics';


const SignUp = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [userType, setUserType] = useState(searchParams.get('type') || 'contractor');
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    location: '',
    company: '',
    skills: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast({
        title: 'Error',
        description: 'Passwords do not match!',
        variant: 'destructive'
      });
      return;
    }

    if (formData.password.length < 6) {
      toast({
        title: 'Error',
        description: 'Password must be at least 6 characters',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      const { authAPI } = await import('../api/api');
      const userData = {
        email: formData.email,
        password: formData.password,
        user_type: userType,
        name: formData.name,
        phone: formData.phone,
        location: formData.location || '',
        ...(userType === 'contractor' 
          ? { company: formData.company } 
          : userType === 'worker'
          ? { skills: formData.skills ? formData.skills.split(',').map(s => s.trim()) : [] }
          : {})
      };
      
      const response = await authAPI.register(userData);
      
      if (response.data.success) {
        trackConversion(`${response.data.user.user_type}_signup`);
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('userType', response.data.user.user_type);
        localStorage.setItem('userId', response.data.user._id);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        toast({
          title: 'Account Created!',
          description: `Welcome to ConstructHelp! Redirecting to your dashboard...`,
        });

        setTimeout(() => {
          if (response.data.user.user_type === 'contractor') {
            window.location.href = '/contractor-dashboard';
          } else if (response.data.user.user_type === 'homeowner') {
            window.location.href = '/homeowner-dashboard';
          } else {
            window.location.href = '/worker-dashboard';
          }
        }, 1000);
      }
    } catch (error) {
      toast({
        title: 'Registration Failed',
        description: error.response?.data?.detail || 'Failed to create account',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback?role=' + (userType || 'worker');
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-[#F0F0F2] dark:bg-neutral-900 py-12 px-4 transition-colors duration-300" data-testid="signup-page">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-black text-neutral-900 dark:text-white">Create Account</h1>
          <p className="text-neutral-600 dark:text-neutral-400 mt-2">Join ConstructHelp today</p>
        </div>

        {/* User Type Toggle */}
        <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-2 mb-6 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => setUserType('homeowner')}
              className={`h-14 flex items-center justify-center gap-2 font-bold rounded-lg transition-all text-sm ${
                userType === 'homeowner'
                  ? 'bg-[#2B4C6F] text-white'
                  : 'bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-600'
              }`}
              data-testid="homeowner-type-btn"
            >
              <House size={22} weight="bold" />
              Homeowner
            </button>
            <button
              type="button"
              onClick={() => setUserType('contractor')}
              className={`h-14 flex items-center justify-center gap-2 font-bold rounded-lg transition-all text-sm ${
                userType === 'contractor'
                  ? 'bg-[#2B4C6F] text-white'
                  : 'bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-600'
              }`}
              data-testid="contractor-type-btn"
            >
              <Briefcase size={22} weight="bold" />
              Contractor
            </button>
            <button
              type="button"
              onClick={() => setUserType('worker')}
              className={`h-14 flex items-center justify-center gap-2 font-bold rounded-lg transition-all text-sm ${
                userType === 'worker'
                  ? 'bg-[#2B4C6F] text-white'
                  : 'bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-600'
              }`}
              data-testid="worker-type-btn"
            >
              <HardHat size={22} weight="bold" />
              Worker
            </button>
          </div>
        </div>

        {/* Sign Up Card */}
        <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-8 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
          {/* Google Sign Up */}
          <button
            type="button"
            onClick={handleGoogleSignup}
            className="w-full h-14 flex items-center justify-center gap-3 bg-white dark:bg-neutral-700 text-neutral-700 dark:text-white font-bold text-lg rounded-xl border-2 border-black dark:border-neutral-600 shadow-[3px_3px_0px_#000] dark:shadow-[3px_3px_0px_#2B4C6F] hover:shadow-[4px_4px_0px_#000] dark:hover:shadow-[4px_4px_0px_#2B4C6F] active:translate-y-1 active:shadow-none transition-all mb-6"
            data-testid="google-signup-btn"
          >
            <GoogleLogo size={24} weight="bold" className="text-[#4285F4]" />
            Sign up with Google
          </button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t-2 border-neutral-200 dark:border-neutral-700" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 font-bold">or sign up with email</span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="name" className="text-lg font-bold block mb-2 text-neutral-900 dark:text-white">Full Name *</Label>
              <div className="relative">
                <User size={24} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Smith"
                  required
                  className="h-14 pl-12 text-lg border-2 border-black dark:border-neutral-600 rounded-xl dark:bg-neutral-700 dark:text-white"
                  data-testid="name-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-lg font-bold block mb-2 text-neutral-900 dark:text-white">Email Address *</Label>
              <div className="relative">
                <EnvelopeSimple size={24} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your.email@example.com"
                  required
                  className="h-14 pl-12 text-lg border-2 border-black dark:border-neutral-600 rounded-xl dark:bg-neutral-700 dark:text-white"
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="password" className="text-lg font-bold block mb-2 text-neutral-900 dark:text-white">Password *</Label>
                <div className="relative">
                  <Lock size={24} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    required
                    className="h-14 pl-12 text-lg border-2 border-black dark:border-neutral-600 rounded-xl dark:bg-neutral-700 dark:text-white"
                    data-testid="password-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="confirmPassword" className="text-lg font-bold block mb-2 text-neutral-900 dark:text-white">Confirm *</Label>
                <div className="relative">
                  <Lock size={24} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="••••••••"
                    required
                    className="h-14 pl-12 text-lg border-2 border-black dark:border-neutral-600 rounded-xl dark:bg-neutral-700 dark:text-white"
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone" className="text-lg font-bold block mb-2 text-neutral-900 dark:text-white">Phone</Label>
                <div className="relative">
                  <Phone size={24} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                    className="h-14 pl-12 text-lg border-2 border-black dark:border-neutral-600 rounded-xl dark:bg-neutral-700 dark:text-white"
                    data-testid="phone-input"
                  />
                </div>
              </div>
              {userType !== 'homeowner' && (
              <div>
                <Label htmlFor="location" className="text-lg font-bold block mb-2 text-neutral-900 dark:text-white">Location</Label>
                <div className="relative">
                  <MapPin size={24} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                  <Input
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="City, State"
                    className="h-14 pl-12 text-lg border-2 border-black dark:border-neutral-600 rounded-xl dark:bg-neutral-700 dark:text-white"
                    data-testid="location-input"
                  />
                </div>
              </div>
              )}
            </div>

            {/* Contractor-specific field */}
            {userType === 'contractor' && (
              <div>
                <Label htmlFor="company" className="text-lg font-bold block mb-2 text-neutral-900 dark:text-white">Company Name</Label>
                <div className="relative">
                  <Buildings size={24} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                  <Input
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Your Construction Company LLC"
                    className="h-14 pl-12 text-lg border-2 border-black dark:border-neutral-600 rounded-xl dark:bg-neutral-700 dark:text-white"
                    data-testid="company-input"
                  />
                </div>
              </div>
            )}

            {/* Worker-specific field */}
            {userType === 'worker' && (
              <div>
                <Label htmlFor="skills" className="text-lg font-bold block mb-2 text-neutral-900 dark:text-white">Your Skills</Label>
                <div className="relative">
                  <Wrench size={24} weight="bold" className="absolute left-4 top-4 text-neutral-400" />
                  <Textarea
                    id="skills"
                    name="skills"
                    value={formData.skills}
                    onChange={handleChange}
                    placeholder="Carpentry, Plumbing, Electrical..."
                    className="pl-12 text-lg border-2 border-black dark:border-neutral-600 rounded-xl min-h-[80px] dark:bg-neutral-700 dark:text-white"
                    data-testid="skills-input"
                  />
                  <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">Separate skills with commas</p>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full h-16 flex items-center justify-center gap-2 bg-[#2B4C6F] text-white font-bold text-xl rounded-xl border-2 border-black shadow-[4px_4px_0px_#000] hover:shadow-[5px_5px_0px_#000] active:translate-y-1 active:shadow-none transition-all disabled:opacity-50"
              data-testid="submit-btn"
            >
              {loading ? (
                <><SpinnerGap size={24} weight="bold" className="animate-spin" /> Creating Account...</>
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          <p className="mt-6 text-center text-neutral-600 dark:text-neutral-400">
            Already have an account?{' '}
            <Link to="/login" className="text-[#2B4C6F] font-bold hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
